import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { useLanguage } from "../../contexts/LanguageContext";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  testId?: string;
  className?: string;
  formId?: string;
  submitLabel: string;
  isSubmitting?: boolean;
  localeTab?: "fr" | "en";
  onLocaleChange?: (tab: "fr" | "en") => void;
  showLocaleTabs?: boolean;
  onSubmitClick?: () => void;
  children: React.ReactNode;
};

export const AdminFormDialog: React.FC<Props> = ({
  open,
  onClose,
  title,
  subtitle,
  testId,
  className,
  formId,
  submitLabel,
  isSubmitting,
  localeTab = "fr",
  onLocaleChange,
  showLocaleTabs,
  onSubmitClick,
  children,
}) => {
  const { t } = useLanguage();

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className={cn("admin-form-dialog max-w-2xl p-0 gap-0 overflow-hidden sm:rounded-xl", className)}
        data-testid={testId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-form-dialog__header">
          <DialogHeader className="space-y-1.5 p-6 pb-4 pr-14">
            <DialogTitle className="text-xl font-bold text-[#041797] leading-tight">{title}</DialogTitle>
            {subtitle && (
              <DialogDescription className="text-sm text-muted-foreground">{subtitle}</DialogDescription>
            )}
          </DialogHeader>
          {showLocaleTabs && onLocaleChange && (
            <div className="locale-tabs" role="tablist" aria-label="Language">
              <button
                type="button"
                role="tab"
                aria-selected={localeTab === "fr"}
                className={localeTab === "fr" ? "active" : ""}
                onClick={() => onLocaleChange("fr")}
              >
                {t.admin.french}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={localeTab === "en"}
                className={localeTab === "en" ? "active" : ""}
                onClick={() => onLocaleChange("en")}
              >
                {t.admin.english}
              </button>
            </div>
          )}
        </div>

        <div className="admin-form-dialog__body px-6 pb-2">{children}</div>

        <DialogFooter className="admin-form-dialog__footer">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t.admin.cancel}
          </Button>
          <Button
            type={onSubmitClick ? "button" : "submit"}
            form={onSubmitClick ? undefined : formId}
            disabled={isSubmitting}
            className="min-w-[100px] shadow-md"
            onClick={onSubmitClick}
          >
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AdminField: React.FC<{
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}> = ({ label, required, error, children }) => (
  <div className="admin-field">
    <label className="admin-field__label">
      {label}
      {required && <span className="text-destructive"> *</span>}
    </label>
    {children}
    {error && <p className="text-sm text-destructive mt-1">{error}</p>}
  </div>
);
